/**
 * RFC 0292 — naming a session wakes it.
 *
 * The delivery half of a mention, and deliberately the same act
 * `answerComment` performs for a reply: append an event to the named session's
 * log and queue a run. RFC 0289 could only trigger that from a reply, because a
 * reply is the one edge threading already implies — the parent has an author and
 * that author is a session. A comment that STARTS a thread has no parent, which
 * is why a question nobody had already answered reached nobody. This is the
 * missing edge, drawn explicitly by the person writing.
 *
 * WHY IT IS NOT INSIDE `createComment`. That is a write and stays one. Waking a
 * session is a policy decision with a queued inference run behind it, and
 * folding the two together would mean every caller that happens to write a
 * comment also queues turns.
 *
 * WHY ONE FUNCTION AND NOT A HOOK PER SURFACE, exactly as `answerComment` says:
 * three surfaces write comments (the agent's tool, the sandboxed bridge, the
 * host-realm apps), and a notification rule written three times is a rule that
 * holds in two of them, failing silently in the third.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import type { Mention } from '../../model/comments/types'
import { projectAuthor } from '../../model/people/reads'
import type { ProjectDoc } from '../../model/project/types'
import { projectSessions } from '../../model/session/reads'
import { enqueueMentionNotification } from '../run/enqueue-run'

/** One line of the prose rides along, matching `CommentRepliedData.summary`. */
const SUMMARY_MAX = 280

export interface MentionDelivery {
  /** The session named. */
  sessionId: string
  /** Whether a run was queued. False for a captured session: the event is on
   *  its log, but its responder is external and drains no mod runs. */
  woken: boolean
}

export interface DeliverMentionsResult {
  /** Every session that was told, in the order the mentions appear. */
  delivered: MentionDelivery[]
  /**
   * Why a mention woke nobody, when it did not. Returned rather than logged,
   * for `AnswerCommentResult`'s reason: a UI can stay silent while a test wants
   * to assert that naming yourself queued nothing.
   */
  skipped: Array<{
    mention: Mention
    reason: 'document' | 'self-mention' | 'unknown-session' | 'duplicate'
  }>
}

/**
 * Walk a comment's mentions and wake the sessions they name.
 *
 * The three guards matter as much as the delivery, and each of them is a loop
 * or a lie if dropped:
 *
 *  - A session that names ITSELF wakes nobody. Otherwise a turn queues a run to
 *    read text it just wrote, and is free to write it again.
 *  - A mention of a session that no longer exists is DROPPED, not thrown. The
 *    comment is already written; failing here would report an error about a
 *    write that succeeded, and there is nothing the person could do about a
 *    session somebody else deleted.
 *  - A document mention delivers nothing at all. Naming a document is a link
 *    (RFC 0292 §3d); naming a session is the only thing here that causes work.
 *
 * Naming the same session twice in one comment wakes it ONCE. The second chip
 * is a second reference to the same conversation, not a second reason to run.
 */
export async function deliverMentions(
  repo: Repo,
  projectId: string,
  opts: {
    /** The comment carrying them — the thread an agent answers in. */
    commentId: string
    mentions: Mention[] | undefined
    author: string
    body: string
    documentId?: string
    anchorText?: string
    /** The object within `documentId` the comment is on, for a document with no
     *  prose to quote. Carried through so the woken session can list that one
     *  thread rather than every comment on the canvas. */
    nodeId?: string
    /** The session that WROTE the comment, when an agent did. Used only to keep
     *  it from waking itself. */
    authorSessionId?: string
  },
): Promise<DeliverMentionsResult> {
  const out: DeliverMentionsResult = { delivered: [], skipped: [] }
  const mentions = opts.mentions ?? []
  if (mentions.length === 0) return out

  const handle = await repo.find<ProjectDoc>(projectId as never)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const known = new Set(projectSessions(handle.doc()).map((s) => s.id))

  const seen = new Set<string>()
  for (const mention of mentions) {
    if (mention.kind !== 'session') {
      out.skipped.push({ mention, reason: 'document' })
      continue
    }
    if (mention.id === opts.authorSessionId) {
      out.skipped.push({ mention, reason: 'self-mention' })
      continue
    }
    if (!known.has(mention.id)) {
      out.skipped.push({ mention, reason: 'unknown-session' })
      continue
    }
    if (seen.has(mention.id)) {
      out.skipped.push({ mention, reason: 'duplicate' })
      continue
    }
    seen.add(mention.id)
    const woken = await enqueueMentionNotification(
      repo,
      mention.id,
      {
        commentId: opts.commentId,
        ...(opts.documentId ? { documentId: opts.documentId } : {}),
        // The writer's NAME, not their id: this is the line an agent reads.
        author: projectAuthor(handle.doc(), {
          author: opts.author,
          ...(opts.authorSessionId
            ? { authorSessionId: opts.authorSessionId }
            : {}),
        }).label,
        ...(opts.anchorText ? { anchorText: opts.anchorText } : {}),
        ...(opts.nodeId ? { nodeId: opts.nodeId } : {}),
        summary: summarise(opts.body),
      },
      { projectId },
    )
    out.delivered.push({ sessionId: mention.id, woken })
  }
  return out
}

/** First line, bounded. The comment is read in the thread; this is the nudge. */
function summarise(body: string): string {
  const line = body.trim().split('\n', 1)[0] ?? ''
  return line.length > SUMMARY_MAX ? `${line.slice(0, SUMMARY_MAX - 1)}…` : line
}
